import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import styled from 'styled-components';
import { usePopupLogin } from '../../hooks/usePopupLogin';
import { CheckCircle, XCircle } from 'lucide-react';
import { inviteService } from '@/services/inviteService';
import { PitchReviewerInvite, InvitationStep } from '@/common/types/invitation';
import { toastUtils } from '@/common/utils/toastUtils';
import NotFound from '../NotFound';
import { useNavigationStore } from '@/common/store';
import { extractDateTime } from '@/common/utils/dateFormat';
import InisisAuthButton, { InisisAuthResult } from '@/components-common/inisis/InisisAuthButton';
import { semanticColors } from '@/common/styles/colorTokens';
import { InvitationLayout } from '@/pages/invitation/layout/InvitationLayout';
import Section from '@/common/components/section/Section';
import { Button } from '@/common/components/button/Button';
import { LabelInput } from '@/common/components/input/LabelInput';
import ModalCenter from '@/common/components/modal/ModalCenter';
import { authService } from '@/services/authService';
import { getBirthGender } from '@/common/utils/commonUtil';

const InvitationPitchingPage: React.FC = () => {
  const navigate = useNavigate();
  const {  } = useNavigationStore();
  const [searchParams] = useSearchParams();

  // 상태 관리
  const [currentStep, setCurrentStep] = useState<InvitationStep>('INITIAL');
  const [differentUser, setDifferentUser] = useState<boolean>(false);
  const [inviteInfo, setInviteInfo] = useState<PitchReviewerInvite | null>(null);
  const [notificationId, setNotificationId] = useState<string | null>(null);
  const [isPageNotFound, setIsPageNotFound] = useState<boolean>(false);
  const [rejectReason, setRejectReason] = useState<string>('');
  const [showRejectModal, setShowRejectModal] = useState<boolean>(false);

  // 회원가입 관련 상태
  const [isCertifiedPersonal, setIsCertifiedPersonal] = useState<boolean>(false);
  const [openCertifyPopup, setOpenCertifyPopup] = useState<boolean>(false);
  const [password, setPassword] = useState<string>('');
  const [passwordConfirm, setPasswordConfirm] = useState<string>('');
  const [formData, setFormData] = useState({
    loginId: '',
    notificationId: '',
    birthGender: '',
    name: '',
    phone: '',
    email: '',
    emailVerified: false,
    password: '',
    company: '',
    userDi: ``,
    userCi: ``,
    token: ``,
  });

  // 초기 상태에 surveyStep 추가
  const [surveyStep, setSurveyStep] = useState<'PARTICIPATION' | 'MEMBERSHIP'>('PARTICIPATION');

  // 로그인 팝업
  const { openPopup } = usePopupLogin({
    onSuccess: async (data) => {
      try {
        // 사용자 정보 확인
        const responseUser = await authService.setUserProfile();
        
        if(!responseUser.success || !responseUser.data) {
          toastUtils.error('사용자 정보를 가져오는 데 실패했습니다.');
          return;
        }
        
        if(!inviteInfo) {
          toastUtils.error('초대 정보를 가져오는 데 실패했습니다.');
          return;
        }

        // 이름과 이메일 확인
        const isNameMatch = responseUser.data.name === inviteInfo.content?.name;
        const isEmailMatch = responseUser.data.email === inviteInfo.content?.email || 
                            responseUser.data.companyUsers.some((companyUser: any) => companyUser.email === inviteInfo.content?.email);

        if (!isNameMatch || !isEmailMatch) {
          toastUtils.error('초대된 사용자와 일치하지 않습니다.');
          setDifferentUser(true);
          return;
        }
        
        const result = await inviteService.acceptInviteForLogin(notificationId!);
        if (result?.success) {
          setCurrentStep('COMPLETE');
        }
      } catch (error) {
        console.error('로그인 처리 중 오류 발생:', error);
        toastUtils.error('로그인 처리 중 오류가 발생했습니다.');
      }
    }
  });

  // 초기 데이터 로드
  const getInviteInfo = async () => {
    const nId = searchParams.get('n_id');
    if (!nId) {
      setIsPageNotFound(true);
      return;
    }

    setNotificationId(nId);
    const response = await inviteService.getInviteInfoForPitchReviewer(nId);
    
    if (response) {
      setInviteInfo(response);
      response.isApproved === undefined ? setCurrentStep('INITIAL') : response.isApproved ? setCurrentStep('COMPLETE') : setCurrentStep('REJECTED');
    } else {
      setIsPageNotFound(true);
    }
  };

  // 간편인증 처리
  const handleCertify = async (data: InisisAuthResult) => {
    const {isDuplicate, userDi, userCi, userName, userPhone, userBirth, userGender, token} = data;
    if (userName !== inviteInfo?.content?.name) {
      toastUtils.error('초대된 사용자와 일치하지 않습니다.');
      setDifferentUser(true);
      return;
    }

    if (isDuplicate) {
      toastUtils.error(`이미 가입된 번호입니다.`);
      return;
    }

    // 이메일 체크는 회원가입 단계에서 하도록 유지
    setIsCertifiedPersonal(true);
    setFormData(prev => ({
      ...prev,
      name: userName,
      phone: userPhone,
      birthGender: getBirthGender(userBirth, userGender),
      userDi,
      userCi,
      token
    }));
    setOpenCertifyPopup(false);
    setCurrentStep('SIGNUP');
  };

  // 회원가입 완료
  const handleSignupComplete = async () => {
    if (!isCertifiedPersonal) {
      toastUtils.error('간편 인증을 진행해주세요.');
      return;
    }

    if (password !== passwordConfirm) {
      toastUtils.error('비밀번호가 일치하지 않습니다.');
      return;
    }

    const response = await inviteService.acceptInviteForRegister({
      notificationId: notificationId!,
      loginId: formData.loginId,
      residentNumber: formData.birthGender+'******',
      name: formData.name,
      phone: formData.phone,
      email: inviteInfo?.content.email!,
      password: password,
      company: formData.company,
      emailVerified: true,
      userDi: formData.userDi,
      userCi: formData.userCi,
      token: formData.token,
    });

    if (response?.success) {
      setCurrentStep('COMPLETE');
    }
  };

  // 초대 거절
  const handleReject = async () => {
    if (!rejectReason.trim()) {
      toastUtils.error('거절 사유를 입력해주세요.');
      return;
    }

    const response = await inviteService.rejectInvite({
      notificationId: notificationId!,
      reason: rejectReason
    });

    if (response?.success) {
      getInviteInfo();
      setCurrentStep('REJECTED');
      setShowRejectModal(false);
    }
  };

  // 비밀번호 유효성 검사
  const validatePassword = () => {
    if (passwordConfirm.length === 0 || password.length === 0) 
      return <ValidationMessage $isValid={false}>비밀번호를 입력해주세요.</ValidationMessage>;

    if (passwordConfirm === password) {
      return (
        <ValidationMessage $isValid={true}>
          <CheckCircle size={16} />
          비밀번호가 일치합니다
        </ValidationMessage>
      );
    }
    return (
      <ValidationMessage $isValid={false}>
        <XCircle size={16} />
        비밀번호가 일치하지 않습니다
      </ValidationMessage>
    );
  };

  useEffect(() => {
    
    getInviteInfo();
  }, []);

  if (isPageNotFound) return <NotFound />;
  if (!inviteInfo) return <LoadingContainer><div>초대 정보를 로드 중입니다...</div></LoadingContainer>;

  // Step 1: 초기 화면
  const InitialStep = () => (
    <InvitationLayout maxWidth="600px">
      <Section
        title={`${inviteInfo.inviter.companyName} 피칭심사 심사위원 초대`}
        description={`${inviteInfo.inviter.name}님이 ${inviteInfo.content.name}님을 ${inviteInfo.inviter.companyName}의 피칭심사 심사위원으로 초대했습니다.`}
      >
        {/* 피칭 정보 */}
        <InfoSection>
          <SectionTitle>피칭 정보</SectionTitle>
          <InfoList>
            <InfoItem>
              <InfoLabel>피칭 제목</InfoLabel>
              <InfoValue>{inviteInfo.content.title}</InfoValue>
            </InfoItem>
            <InfoItem>
              <InfoLabel>일시</InfoLabel>
              <InfoValue>{extractDateTime(inviteInfo.content.date)}</InfoValue>
            </InfoItem>
            <InfoItem>
              <InfoLabel>역할</InfoLabel>
              <InfoValue>{inviteInfo.content.role}</InfoValue>
            </InfoItem>
            <InfoItem>
              <InfoLabel>이름</InfoLabel>
              <InfoValue>{inviteInfo.content.name}</InfoValue>
            </InfoItem>
            <InfoItem>
              <InfoLabel>이메일</InfoLabel>
              <InfoValue>{inviteInfo.content.email}</InfoValue>
            </InfoItem>
          </InfoList>
        </InfoSection>

        {differentUser ? (
          <AlertBox $variant="error">
            <XCircle size={20} />
            <AlertText>
              초대된 사용자와 다른 사용자입니다. 자동적으로 거절 처리됩니다.
              <br />
              초대 정보를 확인하시고 틀린 정보가 있다면 자세한 사유를 작성해주세요.
              <small>
                * 사유는 초대자에게 전달됩니다.
              </small>
            </AlertText>
          </AlertBox>
        ) : (
          <DecisionSection>
            {surveyStep === 'PARTICIPATION' && (
              <>
                <DecisionMessage>
                  피칭 심사에 참여하시겠습니까?
                  <small>* 거절하시면 초대자에게 거절 사유가 전달됩니다.</small>
                </DecisionMessage>

                <ButtonGroup>
                  <Button 
                    $variant='outline' 
                    $size='sm'
                    onClick={() => setShowRejectModal(true)}
                  >
                    아니오, 참여하지 않겠습니다
                  </Button>
                  <Button 
                    $variant='fill'
                    $size='sm'
                    onClick={() => setSurveyStep('MEMBERSHIP')}
                  >
                    네, 참여하겠습니다
                  </Button>
                </ButtonGroup>
              </>
            )}

            {surveyStep === 'MEMBERSHIP' && (
              <>
                <DecisionMessage>
                  피칭 심사 참여를 위해서는 회원 가입이 필요합니다.
                  <br />
                  이미 가입하신 계정이 있으신가요?
                </DecisionMessage>

                <ButtonGroup>
                  <InisisAuthButton 
                    $variant='outline'
                    $size='sm'
                    $title='아니오, 새로 가입하겠습니다'
                    onSuccess={handleCertify}
                  />
                  <Button 
                    $variant='fill'
                    $size='sm'
                    onClick={openPopup}
                  >
                    네, 로그인하겠습니다
                  </Button>
                </ButtonGroup>

                <Notice>
                  * 회원가입 시 본인 인증이 필요합니다
                </Notice>
              </>
            )}
          </DecisionSection>
        )}

        {differentUser && (
          <>
            <FormItem>  
              <textarea
                style={{
                  width: '100%', 
                  height: 120, 
                  padding: 16, 
                  border: '1px solid #e2e8f0', 
                  borderRadius: 8,
                  fontFamily: 'inherit',
                  resize: 'none',
                  outline: 'none',
                  transition: 'border-color 0.2s',
                  fontSize: '14px'
                }}
                value={rejectReason}
                onChange={(e) => setRejectReason(e.target.value)}
                placeholder="거절 사유를 입력해주세요"
              />
            </FormItem>

            <ButtonGroup>
              <Button onClick={() => setDifferentUser(false)} $variant='outline' $size='sm'>
                다시하기
              </Button>
              <Button 
                disabled={!rejectReason.trim()} 
                onClick={handleReject}
                $size='sm'
              >
                거절하기
              </Button>
            </ButtonGroup>
          </>
        )}
      </Section>
    </InvitationLayout>
  );

  // Step 2: 회원가입 화면
  const SignupStep = () => (
    <InvitationLayout maxWidth="600px">
      <Section
        title="회원 정보 입력"
        description="회원가입을 위한 정보를 입력해주세요."
      >
        <FormSection>
          <HighlightInfo>
            <HighlightItem>
              <HighlightLabel>이름</HighlightLabel>
              <HighlightValue>{formData.name}</HighlightValue>
            </HighlightItem>
            <HighlightItem>
              <HighlightLabel>전화번호</HighlightLabel>
              <HighlightValue>{formData.phone}</HighlightValue>
            </HighlightItem>
            <HighlightItem>
              <HighlightLabel>이메일</HighlightLabel>
              <HighlightValue>{inviteInfo.content.email}</HighlightValue>
            </HighlightItem>
          </HighlightInfo>

          <FormGroup>
            <LabelInput
              label="아이디"
              type="text"
              value={formData.loginId}
              onChange={(e) => setFormData(prev => ({ ...prev, loginId: e.target.value }))}
              placeholder="아이디를 입력해주세요"
            />
          </FormGroup>
          <FormGroup>
            <LabelInput
              label="비밀번호"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="비밀번호를 입력해주세요"
            />
          </FormGroup>

          <FormGroup>
            <LabelInput
              label="비밀번호 확인"
              type="password"
              value={passwordConfirm}
              onChange={(e) => setPasswordConfirm(e.target.value)}
              placeholder="비밀번호를 다시 입력해주세요"
            />
            {validatePassword()}
          </FormGroup>

          <FormGroup>
            <LabelInput
              label="소속"
              type="text"
              value={formData.company}
              onChange={(e) => setFormData(prev => ({ ...prev, company: e.target.value }))}
              placeholder="소속 회사를 입력해주세요"
            />
          </FormGroup>
        </FormSection>

        <ButtonContainer>
          <Button 
            $variant='fill' 
            $size='sm'
            disabled={!password || !passwordConfirm || password !== passwordConfirm} 
            onClick={handleSignupComplete}
          >
            완료하기
          </Button>
        </ButtonContainer>
      </Section>
    </InvitationLayout>
  );

  // Step 3: 완료 화면
  const CompleteStep = () => (
    <InvitationLayout maxWidth="800px">
      <WelcomeContent>
        <SuccessAnimation>
          <CheckCircle size={64} color="#22C55E" />
        </SuccessAnimation>
        <Section
          title="초대 수락 완료"
          description="초대 수락이 완료되었습니다."
        >
          <CompletedInfo>
            <InfoRow>
              <InfoLabel>피칭 제목</InfoLabel>
              <InfoValue>{inviteInfo.content.title}</InfoValue>
            </InfoRow>
            <InfoRow>
              <InfoLabel>역할</InfoLabel>
              <InfoValue>{inviteInfo.content.role}</InfoValue>
            </InfoRow>
            <InfoRow>
              <InfoLabel>피칭 일시</InfoLabel>
              <InfoValue>{extractDateTime(inviteInfo.content.date)}</InfoValue>
            </InfoRow>
            <InfoRow>
              <InfoLabel>초대자</InfoLabel>
              <InfoValue>{inviteInfo.inviter.companyName}</InfoValue>
            </InfoRow>
            <InfoRow>
              <InfoLabel>수락 일시</InfoLabel>
              <InfoValue>{new Date().toLocaleDateString('ko-KR')} {new Date().toLocaleTimeString('ko-KR', { hour: '2-digit', minute: '2-digit' })}</InfoValue>
            </InfoRow>
          </CompletedInfo>
          
          <WelcomeText>
            로그인하여 서비스를 이용해주세요.
          </WelcomeText>
          
          <Button $variant='fill' onClick={() => navigate('/')} $isFull>
            로그인하기
          </Button>
        </Section>
      </WelcomeContent>
    </InvitationLayout>
  );

  // Step 4: 거절 화면
  const RejectedStep = () => (
    <InvitationLayout maxWidth="600px">
      <WelcomeContent>
        <SuccessAnimation>
          <XCircle size={64} color="#DC3545" />
        </SuccessAnimation>
        <Section
          title="초대 거절 완료"
          description="피칭 심사 초대를 거절했습니다."
        >
          <RejectedInfo>
            <InfoRow>
              <InfoLabel>피칭 제목</InfoLabel>
              <InfoValue>{inviteInfo.content.title}</InfoValue>
            </InfoRow>
            <InfoRow>
              <InfoLabel>회사명</InfoLabel>
              <InfoValue>{inviteInfo.inviter.companyName}</InfoValue>
            </InfoRow>
            <InfoRow>
              <InfoLabel>거절 일시</InfoLabel>
              <InfoValue>{new Date().toLocaleDateString('ko-KR')} {new Date().toLocaleTimeString('ko-KR', { hour: '2-digit', minute: '2-digit' })}</InfoValue>
            </InfoRow>
          </RejectedInfo>
          
          <RejectedNotice>
            <p>거절 사유가 초대자에게 전달되었습니다.</p>
            <p>추후 동일한 회사에서 다시 초대를 받을 수 있습니다.</p>
          </RejectedNotice>

          <Button 
            $variant='fill' 
            onClick={() => navigate('/')} 
            $isFull
            style={{ marginTop: '24px' }}
          >
            메인 페이지로 이동
          </Button>
        </Section>
      </WelcomeContent>
    </InvitationLayout>
  );

  return (
    <>
      {currentStep === 'INITIAL' && InitialStep()}
      {currentStep === 'SIGNUP' && SignupStep()}
      {currentStep === 'COMPLETE' && CompleteStep()}
      {currentStep === 'REJECTED' && RejectedStep()}

      {/* 거절 사유 모달 */}
      <ModalCenter
        isOpen={showRejectModal}
        title="초대 거절"
        maxWidth="500px"
        onClose={() => {
          setShowRejectModal(false);
          setRejectReason('');
        }}
        footer={
          <ButtonGroup style={{justifyContent: 'flex-end'}}>
            <Button 
              $variant='outline' 
              $size='sm'
              onClick={() => {
                setShowRejectModal(false);
                setRejectReason('');
              }}
            >
              취소
            </Button>
            <Button 
              $variant='fill' 
              $size='sm'
              disabled={!rejectReason.trim()} 
              onClick={handleReject}
            >
              거절하기
            </Button>
          </ButtonGroup>
        }
      >
        <RejectModalContent>
          <RejectDescription>
            초대를 거절하는 사유를 입력해주세요.<br />
            작성하신 내용은 초대자에게 전달됩니다.
          </RejectDescription>
          
          <FormGroup>
            <RejectReasonLabel>거절 사유</RejectReasonLabel>
            <RejectReasonTextarea
              value={rejectReason}
              onChange={(e) => {
                const value = e.target.value;
                if (value.length <= 300) {
                  setRejectReason(value);
                }
              }}
              placeholder="거절 사유를 자세히 입력해주세요. (예: 일정상 참석이 어려워서, 전문 분야가 달라서 등)"
              rows={4}
              maxLength={300}
            />
            <CharacterCount $isNearLimit={rejectReason.length > 200}>
              {rejectReason.length}/300자
            </CharacterCount>
          </FormGroup>
        </RejectModalContent>
      </ModalCenter>
    </>
  );
};

// Styled Components
const LoadingContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  height: 100vh;
  font-size: 18px;
  color: ${semanticColors.text.tertiary};
`;

const WelcomeContent = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 2rem;
  text-align: center;
`;

const SuccessAnimation = styled.div`
  margin: 0 auto 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 120px;
  height: 120px;
  background: ${semanticColors.background.secondary};
  border-radius: 50%;
  animation: pulse 2s infinite;
  
  @keyframes pulse {
    0% {
      box-shadow: 0 0 0 0 rgba(34, 197, 94, 0.4);
    }
    70% {
      box-shadow: 0 0 0 15px rgba(34, 197, 94, 0);
    }
    100% {
      box-shadow: 0 0 0 0 rgba(34, 197, 94, 0);
    }
  }
`;

const InfoSection = styled.div`
  background: ${semanticColors.background.secondary};
  padding: 20px;
  border-radius: 8px;
  border: 1px solid ${semanticColors.border.primary};
  margin-bottom: 16px;
`;

const SectionTitle = styled.h3`
  font-size: 16px;
  font-weight: 600;
  margin-bottom: 20px;
  color: ${semanticColors.text.primary};
`;

const InfoList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const InfoItem = styled.div`
  display: flex;
  margin-bottom: 8px;
  font-size: 14px;
  
  span:first-child {
    width: 80px;
    color: ${semanticColors.text.tertiary};
    flex-shrink: 0;
  }
  
  span:last-child {
    color: ${semanticColors.text.primary};
    font-weight: 500;
    overflow: hidden;
    text-overflow: ellipsis;
  }
`;

const InfoLabel = styled.span`
  color: ${semanticColors.text.tertiary};
  font-size: 14px;
`;

const InfoValue = styled.span`
  color: ${semanticColors.text.primary};
  font-weight: 500;
  font-size: 14px;
`;

const AlertBox = styled.div<{ $variant: 'error' | 'warning' | 'info' | 'success' }>`
  display: flex;
  align-items: flex-start;
  gap: 0.75rem;
  padding: 1rem;
  border-radius: 12px;
  margin-bottom: 1.5rem;
  background-color: ${props => 
    props.$variant === 'error' ? semanticColors.background.danger.secondary : 
    props.$variant === 'warning' ? semanticColors.background.warning.secondary : 
    props.$variant === 'success' ? semanticColors.background.success.secondary : 
    semanticColors.background.info.secondary};
  border: 1px solid ${props => 
    props.$variant === 'error' ? semanticColors.border.dangerSecondary : 
    props.$variant === 'warning' ? semanticColors.border.warning : 
    props.$variant === 'success' ? semanticColors.border.success : 
    semanticColors.border.info};
  color: ${props => 
    props.$variant === 'error' ? semanticColors.text.onDanger.secondary : 
    props.$variant === 'warning' ? semanticColors.text.onWarning.primary : 
    props.$variant === 'success' ? semanticColors.text.onSuccess.primary : 
    semanticColors.text.onInfo.primary};
`;

const AlertText = styled.div`
  flex: 1;
  font-size: 0.875rem;
  
  small {
    display: block;
    margin-top: 0.5rem;
    opacity: 0.8;
  }
`;

const DecisionSection = styled.div`
  background: ${semanticColors.background.tertiary};
  border: 1px solid ${semanticColors.border.primary};
  padding: 24px;
  border-radius: 8px;
  margin-top: 16px;
`;

const DecisionMessage = styled.div`
  text-align: center;
  color: ${semanticColors.text.primary};
  margin-bottom: 24px;
  line-height: 1.6;
  
  small {
    display: block;
    margin-top: 8px;
    color: ${semanticColors.text.tertiary};
    font-size: 14px;
  }
`;

const FormItem = styled.div`
  margin-bottom: 0.375rem;
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 1rem;
  justify-content: center;
  flex-wrap: wrap;
`;

const Notice = styled.p`
  margin-top: 16px;
  color: ${semanticColors.text.tertiary};
  font-size: 14px;
  text-align: center;
`;

const FormSection = styled.div`
  width: 100%;
  margin-bottom: 24px;
`;

const HighlightInfo = styled.div`
  background: ${semanticColors.background.tertiary};
  border-radius: 6px;
  padding: 16px;
  margin-bottom: 20px;
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const HighlightItem = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const HighlightLabel = styled.span`
  font-size: 12px;
  color: ${semanticColors.text.tertiary};
  text-transform: uppercase;
`;

const HighlightValue = styled.span`
  font-size: 16px;
  font-weight: 600;
  color: ${semanticColors.text.primary};
`;

const FormGroup = styled.div`
  margin-bottom: 16px;
`;

const ButtonContainer = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: flex-end;
  align-items: center;
  padding: 16px 0 0 0;
  gap: 16px;
  width: 100%;
  min-height: 40px;
  flex: none;
  align-self: stretch;
  margin-top: auto;
`;

const ValidationMessage = styled.span<{ $isValid: boolean }>`
  color: ${props => props.$isValid ? semanticColors.text.onSuccess.primary : semanticColors.text.onDanger.secondary};
  font-size: 12px;
  margin-top: 4px;
  display: flex;
  align-items: center;
  gap: 4px;
`;

const WelcomeText = styled.div`
  margin-bottom: 24px;
  color: ${semanticColors.text.tertiary};
  font-size: 14px;
  text-align: center;
`;

const CompletedInfo = styled.div`
  background: ${semanticColors.background.tertiary};
  border-radius: 8px;
  padding: 24px;
  margin-bottom: 24px;
  min-width: 450px;
`;

const InfoRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 8px 0;
  
  &:not(:last-child) {
    border-bottom: 1px solid ${semanticColors.border.primary};
  }
`;

const RejectModalContent = styled.div`
  padding: 24px;
`;

const RejectDescription = styled.div`
  margin-bottom: 24px;
  color: ${semanticColors.text.primary};
  font-size: 14px;
  text-align: center;
`;

const RejectReasonLabel = styled.label`
  display: block;
  margin-bottom: 8px;
  color: ${semanticColors.text.primary};
  font-size: 14px;
`;

const RejectReasonTextarea = styled.textarea`
  width: 100%;
  padding: 12px;
  border: 1px solid ${semanticColors.border.primary};
  border-radius: 6px;
  font-size: 14px;
  font-family: inherit;
  line-height: 1.5;
  resize: vertical;
  min-height: 100px;
  background-color: ${semanticColors.background.secondary};
  color: ${semanticColors.text.primary};
  transition: border-color 0.2s;

  &:focus {
    outline: none;
    border-color: ${semanticColors.background.brand};
    box-shadow: 0 0 0 2px ${semanticColors.background.brand}20;
  }

  &::placeholder {
    color: ${semanticColors.text.tertiary};
  }
`;

const CharacterCount = styled.span<{ $isNearLimit: boolean }>`
  display: block;
  margin-top: 8px;
  color: ${props => props.$isNearLimit ? semanticColors.text.onDanger.primary : semanticColors.text.tertiary};
  font-size: 12px;
`;

const RejectedInfo = styled.div`
  margin-bottom: 24px;
  background: ${semanticColors.background.tertiary};
  border-radius: 8px;
  padding: 24px;
`;

const RejectedNotice = styled.div`
  background: ${semanticColors.background.tertiary};
  border-radius: 8px;
  padding: 24px;
  margin-bottom: 24px;
  
  p {
    margin: 0;
    color: ${semanticColors.text.secondary};
    font-size: 14px;
    line-height: 1.6;
    text-align: center;
    
    &:first-child {
      margin-bottom: 12px;
      font-weight: 500;
    }
    
    &:last-child {
      color: ${semanticColors.text.tertiary};
    }
  }
`;

export default InvitationPitchingPage;



